import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Flame } from "lucide-react";

const LEVEL_CLS = {
  Bronze: "level-bronze", Silver: "level-silver", Gold: "level-gold", "Certified Pro": "level-pro",
};

export default function MechanicDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [convo, setConvo] = useState([]);

  useEffect(() => {
    api.get(`/mechanics/${id}`).then((r) => setData(r.data));
    api.get(`/conversations/${id}`).then((r) => setConvo(r.data));
  }, [id]);

  if (!data) return <div className="p-8 font-mono-data"><span className="ascii-load" /></div>;
  const m = data.mechanic;

  return (
    <div className="p-8 space-y-6" data-testid="mechanic-detail-page">
      <Link to="/mechanics" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-500">
        <ArrowLeft className="w-3.5 h-3.5" /> back to roster
      </Link>

      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">// dossier</div>
          <h1 className="font-display font-black text-3xl mt-1">{m.name}</h1>
          <div className="text-zinc-500 text-sm mt-1 font-mono-data">{m.phone} · {m.workshop_name || "—"} · {m.city || "—"}</div>
        </div>
        <span className={`px-2 py-1 text-xs font-display font-bold border ${LEVEL_CLS[m.skill_level]}`}>
          {m.skill_level.toUpperCase()}
        </span>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "Points", v: m.points, ac: "text-amber-500" },
          { l: "Repairs", v: m.repairs_completed },
          { l: "Lessons", v: m.lessons_completed },
          { l: "Streak", v: m.learning_streak, icon: <Flame className="w-4 h-4 text-amber-500 inline" /> },
        ].map((x) => (
          <div key={x.l} className="gs-card p-4">
            <div className="overline">{x.l}</div>
            <div className={`font-mono-data font-bold text-3xl mt-2 ${x.ac || "text-zinc-100"}`}>
              {x.icon}{x.v}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="gs-card p-5">
          <div className="overline mb-3">Recent Diagnostics</div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {data.diagnostics.map((d) => (
              <div key={d.id} className="border-b border-zinc-900 pb-2">
                <div className="flex justify-between gap-2">
                  <div className="text-sm">{d.query_text}</div>
                  <span className="confidence-badge whitespace-nowrap">{d.confidence}%</span>
                </div>
                <div className="flex gap-1 mt-1">
                  {d.car_number && <span className="font-mono-data text-[10px] text-amber-300 border border-amber-500/40 px-1.5 py-0.5 bg-amber-500/10">{d.car_number}</span>}
                  {d.dtc_code && <span className="dtc-badge">{d.dtc_code}</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-1 whitespace-pre-line">{d.ai_response}</div>
              </div>
            ))}
            {data.diagnostics.length === 0 && <div className="text-zinc-600 text-xs">No diagnostics yet.</div>}
          </div>
        </div>

        <div className="gs-card p-5">
          <div className="overline mb-3">Repair History</div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {data.repairs.map((r) => (
              <div key={r.id} className="border-b border-zinc-900 pb-2">
                <div className="flex justify-between">
                  <div className="text-sm font-medium">{r.vehicle || "Vehicle"} — {r.job_type}</div>
                  <span className="text-[10px] text-zinc-600 font-mono-data">{r.source}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">Parts: {(r.parts || []).join(", ") || "—"}</div>
                {r.raw_transcript && <div className="text-[11px] text-zinc-600 italic mt-1">"{r.raw_transcript}"</div>}
              </div>
            ))}
            {data.repairs.length === 0 && <div className="text-zinc-600 text-xs">No repairs logged.</div>}
          </div>
        </div>
      </div>

      <div className="gs-card p-5">
        <div className="overline mb-3">WhatsApp Conversation History</div>
        <div className="wa-pane p-4 max-h-80 overflow-y-auto space-y-2">
          {convo.map((c) => (
            <div key={c.id} className={`flex ${c.direction === "in" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-md px-3 py-2 text-sm whitespace-pre-line ${c.kind === "system" ? "wa-system" : c.direction === "in" ? "wa-bubble-in" : "wa-bubble-out"}`}>
                {c.kind === "voice" && <div className="text-[10px] opacity-70 mb-1 font-mono-data">voice · transcribed</div>}
                {c.content}
                {c.meta?.confidence ? <div className="text-[10px] opacity-70 mt-1 font-mono-data">confidence {c.meta.confidence}%</div> : null}
              </div>
            </div>
          ))}
          {convo.length === 0 && <div className="text-zinc-600 text-xs">No conversation yet.</div>}
        </div>
      </div>
    </div>
  );
}
